import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import jwtDecode from 'jwt-decode';
import { SentryService } from '../services/sentry.service';
import { Roles } from './roles';

@Injectable()
export class AuthService {
    constructor(private router: Router, private sentry: SentryService) {}

    public navigateToUrl(url: string) {
        this.router.navigate([url || '/']);
    }

    public getRedirectPath(): string | null {
        return localStorage.getItem('redirect');
    }

    public getRolesArray(): Roles[] {
        const token = this.decodeToken();
        if (!token) {
            return [];
        }

        const roles = token['cognito:groups'] as Roles[];
        return roles ?? [];
    }

    public getToken(): string {
        return localStorage.getItem('id_token') ?? '';
    }

    public getUsername(): string {
        return this.decodeToken()?.['cognito:username'] ?? '';
    }

    public getUserProfile() {
        const decodedToken = this.decodeToken();
        return {
            email: decodedToken?.email ?? '',
            username: decodedToken?.['cognito:username'] ?? '',
            name: decodedToken?.name ?? '',
        };
    }

    public isLoggedIn(): boolean {
        const token = this.getToken();
        const isTokenWellFormed = this.isTokenWellFormed(token);
        const isTokenExpired = this.isTokenExpired(token);

        return !(!token || !isTokenWellFormed || isTokenExpired);
    }

    public setRedirectPath(path: string) {
        return localStorage.setItem('redirect', path);
    }

    public setToken(token: string) {
        localStorage.setItem('id_token', token);
    }

    public signOut() {
        sessionStorage.clear();
        this.unsetToken();
        this.sentry.resetScope();
        this.router.navigateByUrl('/');
    }

    public unsetToken() {
        localStorage.removeItem('id_token');
    }

    /**
     * The type is hard coded here because no one from external world should know what exactly is inside the token.
     */
    private decodeToken(): {
        'cognito:groups': string[];
        'cognito:username': string;
        email: string;
        name: string;
    } | null {
        const token = this.getToken();

        return token ? jwtDecode(token) : null;
    }

    private isTokenExpired(token?: string): boolean {
        if (!token) {
            return true;
        }

        const decoded: { exp: string } = jwtDecode(token);

        const expirationEpoch = parseInt(decoded.exp);

        if (!expirationEpoch || isNaN(expirationEpoch)) {
            return true;
        }

        return new Date().getTime() / 1000 > expirationEpoch;
    }

    private isTokenWellFormed(token: string) {
        const [header, body] = token.split('.');

        if (!header || !body) {
            return false;
        }

        try {
            const headerString = window.atob(this.base64UrlToBase64(header));
            const headerParsed: { kid: unknown } = JSON.parse(headerString);

            const bodyString = window.atob(this.base64UrlToBase64(body));
            const bodyParsed: { at_hash: unknown } = JSON.parse(bodyString);

            return headerParsed.kid && bodyParsed.at_hash;
        } catch (e) {
            return false;
        }
    }

    private base64UrlToBase64(base64Url: string) {
        return base64Url.replace(/-/g, '+').replace(/_/g, '/');
    }
}
